import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, FolderOpen, Wallet, Sparkles, Check, Users, ListChecks } from 'lucide-react';
import CourtPlanLogo from '../components/CourtPlanLogo';

const FEATURES = [
  {
    icon: CalendarClock,
    title: 'Αυτόματες προθεσμίες',
    text: 'Καταχωρείς τη δικαστική πράξη και οι προθεσμίες υπολογίζονται αυτόματα — 76 πρότυπα διαδικασιών και 162 νομικοί κανόνες, πάντα ενημερωμένοι.',
  },
  {
    icon: FolderOpen,
    title: 'Πρόγραμμα ημέρας',
    text: 'Δικάσιμοι, ραντεβού και εργασίες σε μία οθόνη. Ό,τι δεν προλάβεις μεταφέρεται και τίποτα δεν χάνεται.',
  },
  {
    icon: Sparkles,
    title: 'AI Organiser',
    text: 'Με ένα κλικ, οι εκκρεμότητές σου κατανέμονται στις επόμενες ημέρες με σωστή νομική σειρά και ρεαλιστικές διάρκειες.',
  },
  {
    icon: Users,
    title: 'Οργάνωση πελατών και υποθέσεων',
    text: 'Κάθε πελάτης με τις υποθέσεις του σε έναν φάκελο — στοιχεία, αντίδικοι, δικάσιμοι και προθεσμίες, όλα συγκεντρωμένα.',
  },
  {
    icon: ListChecks,
    title: 'Πλήρης καταγραφή ενεργειών ανά υπόθεση',
    text: 'Κάθε ενέργεια καταγράφεται στην υπόθεσή της — τι έγινε, πότε και από ποιον, με πλήρες ιστορικό ανά φάκελο.',
  },
  {
    icon: Wallet,
    title: 'Χρονοχρέωση & Οικονομικά',
    text: 'Χρονόμετρο ανά εργασία, χρεώσεις ανά πελάτη και πλήρης εικόνα οφειλών — χωρίς λογιστικά φύλλα.',
  },
];

const STEPS = [
  { n: '1', title: 'Καταχωρείς την πράξη', text: 'Αγωγή, έφεση, ανακοπή — επιλέγεις από έτοιμα πρότυπα σε δευτερόλεπτα.' },
  { n: '2', title: 'Υπολογίζονται οι προθεσμίες', text: 'Όλη η αλυσίδα ενεργειών με ημερομηνίες λήξης, αυτόματα και με ασφάλεια.' },
  { n: '3', title: 'Οργανώνεται η εβδομάδα σου', text: 'Το πρόγραμμα ημέρας και ο AI Organiser κατανέμουν τη δουλειά — εσύ δικηγορείς.' },
];

const PLANS = [
  {
    name: 'Η εγγραφή είναι ΔΩΡΕΑΝ',
    price: '0 €',
    sub: 'Λαμβάνεις 100 δωρεάν μονάδες',
    items: ['5 μονάδες ανά νέα καταχώρηση', '5 μονάδες ανά χρήση του AI Organiser', 'Όλες οι υπόλοιπες ενέργειες δωρεάν'],
    cta: 'ΔΟΚΙΜΑΣΤΕ ΔΩΡΕΑΝ',
    highlight: false,
  },
  {
    name: 'Μονάδες χρήσης',
    price: '20 €',
    sub: 'ανά πακέτο 100 μονάδων',
    items: ['5 μονάδες για κάθε νέα καταχώρηση', '5 μονάδες ανά χρήση του AI Organiser', 'Όλες οι υπόλοιπες ενέργειες δωρεάν'],
    cta: 'ΞΕΚΙΝΗΣΤΕ',
    highlight: false,
  },
  {
    name: 'Πλήρης έκδοση',
    price: '240 €',
    sub: 'ανά έτος · απεριόριστη χρήση',
    items: ['Απεριόριστες καταχωρήσεις', 'Απεριόριστος AI Organiser', 'Ιδανική για καθημερινή χρήση γραφείου'],
    cta: 'ΑΠΕΡΙΟΡΙΣΤΗ ΧΡΗΣΗ',
    highlight: true,
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [showSloganLeft, setShowSloganLeft] = useState(false);
  const [showSloganRight, setShowSloganRight] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [showStat, setShowStat] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    const leftTimer = setTimeout(() => setShowSloganLeft(true), 300);
    const rightTimer = setTimeout(() => setShowSloganRight(true), 1200);
    const logoTimer = setTimeout(() => setShowLogo(true), 3000);
    const sectionsTimer = setTimeout(() => setShowSections(true), 4500);
    const statTimer = setTimeout(() => setShowStat(true), 5500);
    const restTimer = setTimeout(() => setShowRest(true), 6500);
    return () => { clearTimeout(leftTimer); clearTimeout(rightTimer); clearTimeout(statTimer); clearTimeout(logoTimer); clearTimeout(sectionsTimer); clearTimeout(restTimer); };
  }, []);

  const goLogin = () => { window.scrollTo(0, 0); navigate('/login'); };
  const goRegister = () => { window.scrollTo(0, 0); navigate('/login', { state: { register: true } }); };

  return (
    <div className="min-h-screen bg-[#f7f2e7] overflow-x-hidden">
      {/* ===== HERO ===== */}
      <section className="flex flex-col items-center justify-center px-4 pt-16 sm:pt-24 pb-0 text-center">
        <div className="max-w-5xl w-full">
          <div className="mb-3 sm:mb-4 relative h-20 sm:h-36">
            <div className="absolute inset-0 flex flex-col justify-center">
              <h1
                className={`text-lg sm:text-2xl md:text-3xl lg:text-4xl font-light text-slate-700 leading-relaxed transition-all duration-1000 ease-out ${
                  showSloganLeft ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'
                }`}
              >
                Επικεντρώσου στην ουσία,
              </h1>
              <h1
                className={`text-lg sm:text-2xl md:text-3xl lg:text-4xl font-normal text-slate-900 leading-relaxed transition-all duration-1000 ease-out ${
                  showSloganRight ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
                }`}
              >
                αναλαμβάνουμε τα υπόλοιπα.
              </h1>
            </div>
          </div>

          {/* 2. Logo — εμφανίζεται μετά το σύνθημα */}
          <div className={`transition-all duration-1000 ease-out ${showLogo ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mb-3 sm:mb-4 flex justify-center">
              <CourtPlanLogo size="hero" />
            </div>
          </div>

          {/* 3. Απλά, γρήγορα, αξιόπιστα + Δοκιμάστε δωρεάν */}
          <div className={`transition-all duration-1000 ease-out ${showSections ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-sm sm:text-xl md:text-2xl text-slate-600 font-normal mb-4 sm:mb-6">
              Απλά, γρήγορα, αξιόπιστα
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                data-testid="landing-login-btn"
                onClick={goLogin}
                className="w-full sm:w-auto px-8 py-2.5 text-sm sm:text-lg font-semibold text-white bg-red-700 rounded-lg hover:bg-red-800 transition-all duration-300 tracking-wide"
              >
                ΕΙΣΟΔΟΣ
              </button>
              <button
                data-testid="landing-register-btn"
                onClick={goRegister}
                className="w-full sm:w-auto px-8 py-2.5 text-sm sm:text-lg font-semibold text-slate-800 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-all duration-300 tracking-wide"
              >
                ΔΟΚΙΜΑΣΤΕ ΔΩΡΕΑΝ
              </button>
            </div>
            <p className="mt-3 text-xs sm:text-sm text-slate-500">100 δωρεάν μονάδες με την εγγραφή — χωρίς χρήση κάρτας</p>
          </div>

          {/* 4. Αυτόματος υπολογισμός — κεντραρισμένος καθ' ύψος μεταξύ «χωρίς χρήση κάρτας» και «Τι κάνει διαφορετικά» */}
          <div className={`transition-all duration-1000 ease-out ${showStat ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <p data-testid="landing-deadlines-stat" className="my-12 sm:my-16 text-base sm:text-xl md:text-2xl text-slate-700">
              Αυτόματος υπολογισμός <span className="font-extrabold text-red-700">162 προθεσμιών</span> και προγραμματισμός ενεργειών με χρήση <span className="font-extrabold text-red-700">AI</span> για κάθε Αστική, Ποινική και Διοικητική διαδικασία
            </p>
          </div>

        </div>
      </section>

      {/* 5. Τα υπόλοιπα — εμφανίζονται μετά τον Αυτόματο υπολογισμό */}
      <div className={`transition-opacity duration-1000 ease-out ${showRest ? 'opacity-100' : 'opacity-0'}`}>

      {/* ===== ΤΙ ΚΑΝΕΙ ΔΙΑΦΟΡΕΤΙΚΑ ===== */}
      <section className="px-4 pb-12 sm:pb-16" data-testid="landing-features-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 text-center mb-2">Τι κάνει διαφορετικά το COURTPLAN</h2>
          <p className="text-sm text-slate-500 text-center mb-8">Δεν είναι άλλο ένα πρόγραμμα καταχώρησης — δουλεύει για εσένα</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm text-left">
                <div className="w-11 h-11 rounded-xl bg-red-700/10 text-red-700 flex items-center justify-center mb-3">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1.5">{f.title}</h3>
                <p className="text-sm text-slate-600" style={{ lineHeight: 1.55 }}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ΠΩΣ ΛΕΙΤΟΥΡΓΕΙ ===== */}
      <section className="px-4 py-12 sm:py-16" data-testid="landing-steps-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 text-center mb-8">Πώς λειτουργεί</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="text-center px-2">
                <div className="w-10 h-10 rounded-full bg-red-700 text-white font-bold flex items-center justify-center mx-auto mb-3">{s.n}</div>
                <h3 className="font-semibold text-slate-900 mb-1.5">{s.title}</h3>
                <p className="text-sm text-slate-600" style={{ lineHeight: 1.55 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ΜΑΡΤΥΡΙΕΣ ===== */}
      <section className="px-4 py-12 sm:py-16" data-testid="landing-testimonials-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 text-center mb-8">Τι λένε οι δικηγόροι</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { q: 'Καταχωρώ την αγωγή και οι προθεσμίες βγαίνουν μόνες τους. Έχω σταματήσει να φοβάμαι μη μου ξεφύγει κάτι.', n: 'Μαρία Κ.', r: 'Δικηγόρος Αθηνών, Πολιτικές υποθέσεις' },
              { q: 'Ο AI Organiser μού στήνει την εβδομάδα σε δευτερόλεπτα. Το πρόγραμμα ημέρας έγινε η πρώτη οθόνη που ανοίγω κάθε πρωί.', n: 'Γιώργος Π.', r: 'Δικηγόρος Θεσσαλονίκης' },
              { q: 'Με τη χρονοχρέωση βλέπω επιτέλους πού πάει ο χρόνος μου — και τι πρέπει να τιμολογήσω. Απλό και ελληνικό.', n: 'Ελένη Δ.', r: 'Δικηγορικό γραφείο Πειραιά' },
            ].map((t) => (
              <div key={t.n} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm text-left flex flex-col">
                <p className="text-red-700 text-2xl font-extrabold leading-none mb-2">“</p>
                <p className="text-sm text-slate-700 flex-1" style={{ lineHeight: 1.6 }}>{t.q}</p>
                <div className="mt-4 pt-3 border-t border-slate-100">
                  <p className="text-sm font-semibold text-slate-900">{t.n}</p>
                  <p className="text-xs text-slate-500">{t.r}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ΤΙΜΟΛΟΓΗΣΗ ===== */}
      <section className="px-4 py-12 sm:py-16" data-testid="landing-pricing-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 text-center mb-2">Απλή, ξεκάθαρη τιμολόγηση</h2>
          <p className="text-sm text-slate-500 text-center mb-8">Ξεκίνα δωρεάν — αναβάθμισε μόνο αν σου χρειάζεται</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 items-stretch">
            {PLANS.map((p) => (
              <div
                key={p.name}
                data-testid={`landing-plan-${p.highlight ? 'unlimited' : p.price === '0 €' ? 'free' : 'credits'}`}
                className={`rounded-2xl p-5 sm:p-6 flex flex-col text-left border ${
                  p.highlight
                    ? 'bg-red-700 border-red-700 text-white shadow-[0_16px_40px_-16px_rgba(185,28,28,0.6)]'
                    : 'bg-white border-slate-200 shadow-sm'
                }`}
              >
                <h3 className={`font-semibold mb-1 ${p.highlight ? 'text-white' : 'text-slate-900'}`}>{p.name}</h3>
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className={`text-3xl font-extrabold ${p.highlight ? 'text-white' : 'text-slate-900'}`}>{p.price}</span>
                </div>
                <p className={`text-xs mb-4 ${p.highlight ? 'text-red-100' : 'text-slate-500'}`}>{p.sub}</p>
                <ul className="space-y-2 mb-5 flex-1">
                  {p.items.map((it) => (
                    <li key={it} className={`flex items-start gap-2 text-sm ${p.highlight ? 'text-red-50' : 'text-slate-600'}`}>
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${p.highlight ? 'text-white' : 'text-red-700'}`} />
                      {it}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goRegister}
                  className={`h-10 rounded-lg text-sm font-semibold transition-all ${
                    p.highlight
                      ? 'bg-white text-red-700 hover:bg-red-50'
                      : 'bg-red-700 text-white hover:bg-red-800'
                  }`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ΤΕΛΙΚΟ CTA + FOOTER ===== */}
      <section className="px-4 pt-8 pb-6 text-center" data-testid="landing-footer">
        <p className="text-sm sm:text-base text-slate-700 mb-4">Έτοιμος να αφήσεις τις προθεσμίες σε εμάς;</p>
        <button
          data-testid="landing-footer-register-btn"
          onClick={goRegister}
          className="px-8 py-2.5 text-sm sm:text-base font-semibold text-white bg-red-700 rounded-lg hover:bg-red-800 transition-all"
        >
          ΔΗΜΙΟΥΡΓΙΑ ΔΩΡΕΑΝ ΛΟΓΑΡΙΑΣΜΟΥ
        </button>
        <div className="mt-10 pb-2">
          <p className="text-xs sm:text-sm text-slate-600">
            Μια εφαρμογή της <span className="font-bold text-slate-800">NOVUS CODEX</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">© 2025 COURTPLAN.</p>
        </div>
      </section>

      </div>
    </div>
  );
};

export default LandingPage;
